<template>
  <ak-modal-body v-if="isLoading" class="min-h-350">
    <ak-modal-title v-if="isCreating"> Create Container </ak-modal-title>
    <ak-modal-title v-if="isEditing"> Edit Container </ak-modal-title>
    <ak-loading
      v-model:active="isLoading"
      :can-cancel="false"
      :is-full-page="false"
      :backdrop="false"
    />
  </ak-modal-body>
  <ak-modal-body v-if="!isLoading">
    <ak-modal-title v-if="isCreating"> Create Container </ak-modal-title>
    <ak-modal-title v-if="isEditing"> Edit Container </ak-modal-title>
    <ak-field label="Container Name" required>
      <ak-input v-model="container.title" type="text" required name="title" />
    </ak-field>
    <ak-field label="Project" required>
      <ak-select
        v-model="selected.project"
        label="label"
        name="project"
        :options="options.projects"
        :allow-empty="false"
        required
        @select="selectProject"
      />
    </ak-field>
    <ak-field label="Default Page / Form / A/B Test" required>
      <template #helper>
        The Page / Form / A/B Test to display when no parameters are passed
      </template>
      <ak-select
        v-model="selected.app"
        label="label"
        name="app"
        placeholder=""
        :options="options.published"
        :allow-empty="false"
        required
        @select="selectApp"
      />
    </ak-field>
    <ak-field label="Variants">
      <template #helper>
        Setup the other Pages / Forms / A/B Tests to load through this container
      </template>
    </ak-field>
    <ak-form ref="editVariantForm">
      <ak-display-table v-if="pages.length > 0" cosy rows class="text-small">
        <ak-display-table-row class="text-grey text-bold">
          <ak-display-table-cell header>Name</ak-display-table-cell>
          <ak-display-table-cell header>Type</ak-display-table-cell>
          <ak-display-table-cell header class="nowrap">
            Parameter Key
          </ak-display-table-cell>
          <ak-display-table-cell header class="nowrap">
            Parameter Value
          </ak-display-table-cell>
          <ak-display-table-cell header class="width-75">
            Enabled
          </ak-display-table-cell>
          <ak-display-table-cell header class="width-100" />
        </ak-display-table-row>
        <variant-row
          v-for="(page, key) in pages"
          :key="key"
          v-model:ref-page="page.refPage"
          v-model:ref-experiment="page.refExperiment"
          v-model:type="page.type"
          v-model:status="page.status"
          v-model:tag-key="page.tags[0].key"
          v-model:tag-value="page.tags[0].value"
          :validate="validateEditVariants"
          :valid="editVariantValid"
          :position="page.position"
          :apps="options.apps"
          :params="options.params"
          @delete="deleteVariant"
        />
      </ak-display-table>
    </ak-form>
    <ak-form ref="addVariantFrom" class="flex mt">
      <ak-field horizontal class="flex__item">
        <template #label>
          <span class="text-small text-normal">load</span>
        </template>
        <ak-select
          v-model="variant.load"
          label="label"
          name="load"
          :options="options.apps"
          :show-labels="false"
          :allow-empty="false"
          placeholder=""
          class="width-250"
        >
          <template #singleLabel="{ option }">
            <div
              style="
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
              "
            >
              {{ option.label }}
            </div>
          </template>
          <template #option="{ option }">
            <div
              style="white-space: pre-wrap; overflow-wrap: break-word"
              class="width-200"
            >
              {{ option.label }}
            </div>
          </template>
        </ak-select>
      </ak-field>
      <ak-field horizontal class="ml-tiny flex__item">
        <template #label>
          <span class="text-small text-normal">when</span>
        </template>
        <ak-select
          v-model="variant.when"
          label="label"
          name="when"
          :options="options.params"
          :show-labels="false"
          :allow-empty="false"
          placeholder=""
          class="width-150"
        />
      </ak-field>
      <ak-field horizontal class="ml-tiny flex__item">
        <template #label>
          <span class="text-small text-normal">starts with</span>
        </template>
        <ak-input
          v-model="variant.startsWith"
          type="text"
          name="startsWith"
          placeholder=""
          :custom-rules="startsWithRule"
        />
      </ak-field>
      <ak-button
        ghost
        style="align-self: start"
        class="ml-tiny"
        :disabled="!canAdd"
        @click="addVariant"
      >
        Add
      </ak-button>
    </ak-form>
  </ak-modal-body>
  <ak-modal-footer>
    <ak-modal-button v-if="isCreating" type="submit" :disabled="disabled">
      Create Container
    </ak-modal-button>
    <ak-modal-button
      v-if="isEditing"
      color="success"
      type="submit"
      :disabled="disabled"
    >
      Save Container
    </ak-modal-button>
  </ak-modal-footer>
</template>

<script>
import {
  defineComponent,
  inject,
  ref,
  onBeforeMount,
  computed,
  watch,
  provide,
  nextTick,
} from 'vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import LoadingSpinner from '@akerolabs/akero-vue/src/components/loading/LoadingSpinner.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import Switch from '@akerolabs/akero-vue/src/components/switch/Switch.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import VariantRow from './components/VariantRow.vue'
import InstallContainer from './InstallContainer.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ModalProgrammatic } from '@akerolabs/akero-vue/src/components/modal'
import { success, error, containerParams } from '../utils'
import { accountObj } from '@akerolabs/akero-vue/src/services/accounts.js'
import { agencyObj } from '@akerolabs/akero-vue/src/services/agency.js'
import { isAllowed } from '@akerolabs/akero-vue/src/services/features.js'
export default defineComponent({
  name: 'CreateContainer',
  components: {
    [ModalBody.name]: ModalBody,
    [ModalTitle.name]: ModalTitle,
    [ModalFooter.name]: ModalFooter,
    [ModalButton.name]: ModalButton,
    [Loading.name]: Loading,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [Select.name]: Select,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [LoadingSpinner.name]: LoadingSpinner,
    [Tag.name]: Tag,
    [Switch.name]: Switch,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [VariantRow.name]: VariantRow,
    [InstallContainer.name]: InstallContainer,
  },
  props: {
    edit: {
      type: Object,
      default: undefined,
    },
  },
  setup(props) {
    //- in inject the modal form so we can access it validation and submit functions
    const form = inject('akForm')
    const addVariantFrom = ref(null)
    const isLoading = ref(true)
    const abTesting = ref(false)
    const container = ref({
      account: accountObj()._id,
      agency: agencyObj()._id,
      title: '',
      campaign: '',
    })
    const defaultPage = ref({
      position: 0,
      type: 'page',
      status: 'active',
    })
    const pages = ref([])
    const canAdd = ref(false)
    const editVariantValid = ref(true)
    const editVariantForm = ref(null)
    const disabled = ref(false)

    /**
     * @type {Object}
     * @description object ready for post request
     */
    const containerRequest = computed(() => {
      const variants = pages.value
      for (let i = 0; i < variants.length; i++) {
        if (variants[i].refPage) {
          delete variants[i].refExperiment
        } else {
          delete variants[i].refPage
        }
      }
      return {
        ...container.value,
        pages: [defaultPage.value, ...variants],
      }
    })

    const isCreating = computed(() => {
      return props.edit ? false : true
    })

    const isEditing = computed(() => {
      return props.edit ? true : false
    })

    const options = ref({
      projects: [],
      published: [],
      apps: [],
      params: containerParams,
    })

    const selected = ref({
      project: null,
      app: null,
    })

    const variant = ref({
      load: null,
      when: null,
      startsWith: '',
    })

    watch(
      variant,
      async ({ load, when, startsWith }) => {
        let add = true
        if (!load) add = false
        if (!when) add = false
        if (startsWith.length === 0) add = false
        if (addVariantFrom.value) {
          const valid = await addVariantFrom.value.validate()
          const isValid = !valid.some((i) => i === false)
          if (!isValid) add = false
        }
        canAdd.value = add
      },
      { deep: true },
    )

    /**
     * @param {{value: String}}
     * @description when a project is selected from the project ak-select field
     */
    const selectProject = ({ value }) => {
      container.value.campaign = value
    }

    /**
     * @param {{value: String, modelType: String}}
     * @description when a default page is selected from the default ak-select field
     */
    const selectApp = ({ value, document }) => {
      const updateRef =
        document.modelType === 'App' ? 'refPage' : 'refExperiment'
      const type = document.modelType === 'App' ? 'page' : 'experiment'
      delete defaultPage.value.refPage
      delete defaultPage.value.refExperiment
      defaultPage.value[updateRef] = value
      defaultPage.value.type = type
    }

    /**
     * @description fetch the ak-select options for the project ak-select field
     */
    const fetchProjects = async () => {
      try {
        const { data } = await request.get('/campaigns', {
          params: {
            agency: agencyObj()._id,
            account: accountObj()._id,
            per_page: 0,
            deleted: JSON.stringify({ $ne: true }),
            archived: JSON.stringify({ $ne: true }),
            filters: 'title',
          },
        })
        options.value.projects = data.data.map(({ _id, title }) => {
          return { label: title, value: _id }
        })
      } catch (ex) {
        error('Failed to fetch campaigns', ex)
      }
    }

    /**
     * @description fetch the published app and the experiments (is abTesting is enabled) fro the default ak-select field
     */
    const fetchApps = async () => {
      const published = []
      try {
        const { data: apps } = await request.get('/apps', {
          params: {
            account: accountObj()._id,
            agency: agencyObj()._id,
            per_page: 0,
            filters: 'title, type, publishedStatus',
          },
        })
        let temp = apps.data
        if (abTesting.value) {
          const experiments = await fetchExperiments()
          temp = [...temp, ...experiments]
        }
        options.value.apps = temp.map((document) => {
          const { title, _id, type, status, publishedStatus } = document
          let prefix = ''
          if (type === 'embed') {
            prefix = 'Form'
          } else if (type === undefined) {
            prefix = 'A/B Test'
          } else {
            prefix = 'Page'
          }
          prefix += ' - '
          const obj = {
            label: prefix + title,
            value: _id,
            document,
          }
          if (publishedStatus === true || status === 'active') {
            published.push(obj)
          }
          return obj
        })
        options.value.published = published
      } catch (ex) {
        error('Failed to fetch apps', ex)
      }
    }

    const fetchExperiments = async () => {
      const { data } = await request.get('/experiments', {
        params: {
          account: accountObj()._id,
          agency: agencyObj()._id,
          filters: 'title, status',
          per_page: 0,
        },
      })
      return data.data
    }

    const addVariant = () => {
      const { load, when, startsWith } = variant.value
      let type = ''
      let refType = ''

      if (load.document.modelType === 'App') {
        type = load.document.type === 'embed' ? 'form' : 'page'
        refType = 'refPage'
      } else {
        type = 'experiment'
        refType = 'refExperiment'
      }

      pages.value.push({
        type,
        position: pages.value.length + 1,
        status: 'active',
        tags: [
          {
            key: when.value,
            value: startsWith,
            operator: 'startsWith',
          },
        ],
        [refType]: load.value,
      })
      variant.value = {
        load: null,
        when: options.value.params[0],
        startsWith: '',
      }
    }

    const startsWithRule = [
      (input) => {
        const match = pages.value.find((page) => {
          return (
            page.tags[0].key === variant.value.when.value &&
            page.tags[0].value === variant.value.startsWith
          )
        })
        if (match) {
          input.setCustomValidity(whiteSpace(20) + 'Already in use')
          return false
        } else {
          input.setCustomValidity('')
          return true
        }
      },
    ]

    provide('getPages', () => {
      return pages.value
    })

    const whiteSpace = (count = 1) => {
      let spaces = ''
      for (let i = 0; i < count; i++) {
        spaces += '&nbsp;'
      }
      return spaces
    }

    const validateEditVariants = async () => {
      await nextTick()
      if (editVariantForm.value) {
        const isValid = await editVariantForm.value.validate(true)
        editVariantValid.value = !isValid.some((i) => i === false)
      }
    }

    const deleteVariant = (position) => {
      pages.value = pages.value.filter((page) => {
        return page.position !== position
      })
    }

    form.onBeforeSubmit(async () => {
      if (isCreating.value) {
        return await create()
      } else if (isEditing.value) {
        return await edit()
      }
    })

    const create = async () => {
      disabled.value = true
      isLoading.value = true
      try {
        const create = { ...containerRequest.value }
        const { data } = await request.post('/containers', create)
        setTimeout(() => {
          ModalProgrammatic.open({
            component: InstallContainer,
            outerScroll: true,
            size: 'big',
            props: {
              container: data.data,
            },
          })
        }, 100)
        success('Your Container has been created')
        disabled.value = false
        isLoading.value = false
        return true
      } catch (ex) {
        disabled.value = false
        isLoading.value = false
        error('Failed to create container', ex)
        return false
      }
    }

    const edit = async () => {
      disabled.value = true
      isLoading.value = true
      try {
        const update = { ...containerRequest.value }
        await request.put(`/containers/${update._id}`, update)
        disabled.value = false
        isLoading.value = false
        success('Your container has been updated')
        return true
      } catch (ex) {
        disabled.value = false
        isLoading.value = false
        error('Failed to update container', ex)
        return false
      }
    }

    const loadEdit = () => {
      if (isEditing.value) {
        if (props.edit.pages && props.edit.pages.length > 0) {
          defaultPage.value = props.edit.pages.find(
            ({ position }) => position === 0,
          )
          pages.value = props.edit.pages.filter(
            ({ position }) => position !== 0,
          )
        }
        container.value = props.edit
        delete container.value.pages
        selected.value.project = options.value.projects.find(
          ({ value }) => value === container.value.campaign,
        )
        const findApp = defaultPage.value.refPage
          ? defaultPage.value.refPage
          : defaultPage.value.refExperiment
        if (findApp && findApp.length > 0) {
          selected.value.app = options.value.apps.find(
            ({ value }) => value === findApp,
          )
        }
      }
    }

    /**
     * @description before the component mounts start up the request to gather all the required information
     */
    onBeforeMount(async () => {
      const { setting } = await isAllowed('abTesting')
      if (setting === 'true') {
        abTesting.value = true
      }
      await fetchProjects()
      await fetchApps()
      if (options.value.projects.length > 0) {
        selected.value.project = options.value.projects[0]
        selectProject(options.value.projects[0])
      }
      if (options.value.params.length > 0) {
        variant.value.when = options.value.params[0]
      }
      loadEdit()
      isLoading.value = false
    })

    return {
      addVariantFrom,
      isLoading,
      container,
      pages,
      options,
      selected,
      variant,
      selectProject,
      selectApp,
      canAdd,
      addVariant,
      startsWithRule,
      editVariantForm,
      editVariantValid,
      validateEditVariants,
      deleteVariant,
      disabled,
      isCreating,
      isEditing,
      containerRequest,
    }
  },
})
</script>
